import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { resolveMedia } from "@/lib/media";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Eye, EyeOff, Save } from "lucide-react";

/* ---------- Schema definitions per content kind ---------- */
const SCHEMAS = {
  news: {
    label: "News & Events",
    single: "News Item",
    columns: [
      { key: "cover_image_url", label: "Image", type: "image" },
      { key: "date", label: "Date" },
      { key: "tag", label: "Tag" },
      { key: "title", label: "Title" },
    ],
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "tag", label: "Tag (e.g. R&D, AI, Space)", type: "text" },
      { key: "date", label: "Display date (e.g. Feb 12, 2026)", type: "text" },
      { key: "cover_image_url", label: "Cover Image", type: "image" },
      { key: "excerpt", label: "Short excerpt (1–2 lines)", type: "textarea" },
      { key: "body", label: "Full article", type: "rich" },
    ],
  },
  projects: {
    label: "Projects",
    single: "Project",
    columns: [
      { key: "image_url", label: "Image", type: "image" },
      { key: "status", label: "Status" },
      { key: "year", label: "Year" },
      { key: "title", label: "Title" },
      { key: "domain", label: "Domain" },
    ],
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "status", label: "Status", type: "select", options: ["Completed", "Ongoing", "Future Concept"], required: true },
      { key: "year", label: "Year", type: "text" },
      { key: "domain", label: "Domain (e.g. Aviation, Robotics)", type: "text" },
      { key: "image_url", label: "Cover Image", type: "image" },
      { key: "desc", label: "Short description", type: "textarea" },
      { key: "body", label: "Full write-up", type: "rich" },
    ],
  },
  products: {
    label: "Products",
    single: "Product",
    columns: [
      { key: "image_url", label: "Image", type: "image" },
      { key: "category", label: "Category" },
      { key: "name", label: "Name" },
      { key: "slug", label: "Slug" },
    ],
    fields: [
      { key: "name", label: "Product name", type: "text", required: true },
      { key: "slug", label: "URL slug (e.g. bldc-motor-ak-1200)", type: "text", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "image_url", label: "Product Image", type: "image" },
      { key: "description", label: "Short description", type: "textarea" },
      { key: "applications", label: "Applications (comma separated)", type: "csv" },
      { key: "specs", label: "Specifications (key: value per line)", type: "kv" },
      { key: "body", label: "Full write-up", type: "rich" },
    ],
  },
  "careers-openings": {
    label: "Careers",
    single: "Job Opening",
    columns: [
      { key: "department", label: "Department" },
      { key: "role", label: "Role" },
      { key: "type", label: "Type" },
      { key: "location", label: "Location" },
    ],
    fields: [
      { key: "role", label: "Role", type: "text", required: true },
      { key: "department", label: "Department", type: "text" },
      { key: "type", label: "Type", type: "select", options: ["Full-time", "Part-time", "Internship", "Fellowship", "Contract"] },
      { key: "location", label: "Location", type: "select", options: ["Onsite", "Remote", "Hybrid"] },
    ],
  },
  "training-programs": {
    label: "Training Programs",
    single: "Training Program",
    columns: [
      { key: "tag", label: "Category" },
      { key: "name", label: "Program" },
      { key: "duration", label: "Duration" },
      { key: "level", label: "Level" },
    ],
    fields: [
      { key: "name", label: "Program name", type: "text", required: true },
      { key: "tag", label: "Category (e.g. AI/ML, Robotics)", type: "text" },
      { key: "duration", label: "Duration (e.g. 12 weeks)", type: "text" },
      { key: "level", label: "Level (e.g. Beginner → Advanced)", type: "text" },
    ],
  },
};

const KINDS = Object.keys(SCHEMAS);

const csvToArray = (s) =>
  String(s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const arrayToCsv = (a) => (Array.isArray(a) ? a.join(", ") : "");

const kvToObj = (text) => {
  const out = {};
  String(text || "")
    .split("\n")
    .forEach((line) => {
      const idx = line.indexOf(":");
      if (idx > 0) {
        const k = line.slice(0, idx).trim();
        const v = line.slice(idx + 1).trim();
        if (k) out[k] = v;
      }
    });
  return out;
};

const objToKv = (o) =>
  o && typeof o === "object"
    ? Object.entries(o).map(([k, v]) => `${k}: ${v}`).join("\n")
    : "";

/* ---------- Main Content Manager ---------- */
export const ContentManager = () => {
  const [kind, setKind] = useState("news");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {mode: 'create'|'edit', data: {...}}

  const schema = SCHEMAS[kind];

  const load = useCallback(async (k = kind) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/content/${k}`);
      setItems(data);
    } catch (e) {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    load(kind);
  }, [kind, load]);

  const onCreate = () => {
    setEditing({ mode: "create", data: { published: true } });
  };

  const onEdit = (item) => {
    setEditing({ mode: "edit", data: { ...item } });
  };

  const onTogglePublish = async (item) => {
    try {
      await api.patch(`/admin/content/${kind}/${item.id}`, { published: !item.published });
      setItems((it) => it.map((x) => (x.id === item.id ? { ...x, published: !item.published } : x)));
      toast.success(item.published ? "Moved to draft" : "Published");
    } catch {
      toast.error("Update failed");
    }
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title || item.name || item.role}"?`)) return;
    try {
      await api.delete(`/admin/content/${kind}/${item.id}`);
      setItems((it) => it.filter((x) => x.id !== item.id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const onSave = async (payload) => {
    try {
      if (editing.mode === "create") {
        const { data } = await api.post(`/admin/content/${kind}`, payload);
        setItems((it) => [data, ...it]);
        toast.success("Created");
      } else {
        const { data } = await api.patch(`/admin/content/${kind}/${editing.data.id}`, payload);
        setItems((it) => it.map((x) => (x.id === data.id ? data : x)));
        toast.success("Saved");
      }
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div data-testid="admin-content-manager">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {KINDS.map((k) => (
          <button
            key={k}
            data-testid={`content-tab-${k}`}
            onClick={() => setKind(k)}
            className={`text-sm px-4 py-2 rounded-md border transition-colors ${
              kind === k
                ? "bg-[#0B1B3D] text-white border-[#0B1B3D]"
                : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1B3D]"
            }`}
          >
            {SCHEMAS[k].label}
            <span className="ml-1.5 text-xs opacity-70">
              ({items && kind === k ? items.length : ""})
            </span>
          </button>
        ))}

        <div className="flex-1" />

        <button
          onClick={onCreate}
          data-testid="content-add-button"
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0B1B3D] font-medium text-sm px-4 py-2 rounded-md"
        >
          <Plus className="w-4 h-4" />
          Add {schema.single}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-16 text-center text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <div className="text-sm text-slate-500">No {schema.label.toLowerCase()} yet.</div>
            <button
              onClick={onCreate}
              className="mt-4 inline-flex items-center gap-2 bg-[#0B1B3D] text-white text-sm px-4 py-2 rounded-md"
            >
              <Plus className="w-4 h-4" />
              Create your first {schema.single.toLowerCase()}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3 w-16">Status</th>
                  {schema.columns.map((c) => (
                    <th key={c.key} className="px-4 py-3">
                      {c.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 w-32 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} data-testid={`content-row-${item.id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => onTogglePublish(item)}
                        data-testid={`content-toggle-publish-${item.id}`}
                        title={item.published ? "Published (click to unpublish)" : "Draft (click to publish)"}
                        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${
                          item.published
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-amber-50 text-amber-700 border-amber-200"
                        }`}
                      >
                        {item.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {item.published ? "Live" : "Draft"}
                      </button>
                    </td>
                    {schema.columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 text-sm text-slate-700 max-w-[240px]">
                        {c.type === "image" ? (
                          item[c.key] ? (
                            <img
                              src={resolveMedia(item[c.key])}
                              alt=""
                              className="w-14 h-10 object-cover rounded border border-slate-200"
                            />
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )
                        ) : (
                          <span className="line-clamp-2">{String(item[c.key] || "—")}</span>
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => onEdit(item)}
                          data-testid={`content-edit-${item.id}`}
                          className="p-1.5 rounded hover:bg-slate-100 text-[#0B1B3D]"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onDelete(item)}
                          data-testid={`content-delete-${item.id}`}
                          className="p-1.5 rounded hover:bg-slate-100 text-rose-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <EditorDialog
          schema={schema}
          initial={editing.data}
          onCancel={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
};

/* ---------- Editor Dialog ---------- */
const EditorDialog = ({ schema, initial, onCancel, onSave }) => {
  const [values, setValues] = useState(() => {
    const v = { ...initial };
    // Transform arrays / objects into editable strings
    schema.fields.forEach((f) => {
      if (f.type === "csv") v[f.key] = arrayToCsv(v[f.key]);
      if (f.type === "kv") v[f.key] = objToKv(v[f.key]);
    });
    return v;
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setValues((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    // Required check
    for (const f of schema.fields) {
      if (f.required && !String(values[f.key] || "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    const payload = { ...values };
    schema.fields.forEach((f) => {
      if (f.type === "csv") payload[f.key] = csvToArray(payload[f.key]);
      if (f.type === "kv") payload[f.key] = kvToObj(payload[f.key]);
    });
    setSaving(true);
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="content-editor-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0B1B3D]">
            {initial?.id ? `Edit ${schema.single}` : `Add ${schema.single}`}
          </DialogTitle>
          <DialogDescription>
            Fill in the details below. Toggle "Published" to publish or save as draft.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid gap-5 mt-2">
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
            <div>
              <div className="text-sm font-medium text-[#0B1B3D]">Published</div>
              <div className="text-xs text-slate-500">
                When on, this item is visible on the public website.
              </div>
            </div>
            <Switch
              checked={!!values.published}
              onCheckedChange={(v) => set("published", v)}
              data-testid="content-published-switch"
            />
          </div>

          {schema.fields.map((f) => (
            <div key={f.key}>
              {f.type === "text" && (
                <>
                  <Label htmlFor={`f-${f.key}`}>
                    {f.label}
                    {f.required && <span className="text-rose-500 ml-1">*</span>}
                  </Label>
                  <Input
                    id={`f-${f.key}`}
                    value={values[f.key] || ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`content-field-${f.key}`}
                  />
                </>
              )}
              {f.type === "textarea" && (
                <>
                  <Label htmlFor={`f-${f.key}`}>{f.label}</Label>
                  <Textarea
                    id={`f-${f.key}`}
                    rows={3}
                    value={values[f.key] || ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`content-field-${f.key}`}
                  />
                </>
              )}
              {f.type === "csv" && (
                <>
                  <Label htmlFor={`f-${f.key}`}>{f.label}</Label>
                  <Input
                    id={`f-${f.key}`}
                    placeholder="e.g. Robotics, UAV, Precision CNC"
                    value={values[f.key] || ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`content-field-${f.key}`}
                  />
                </>
              )}
              {f.type === "kv" && (
                <>
                  <Label htmlFor={`f-${f.key}`}>{f.label}</Label>
                  <Textarea
                    id={`f-${f.key}`}
                    rows={5}
                    placeholder={"Rated Power: 1.2 kW\nRated Speed: 6000 RPM"}
                    value={values[f.key] || ""}
                    onChange={(e) => set(f.key, e.target.value)}
                    className="font-mono-akyvia text-sm"
                    data-testid={`content-field-${f.key}`}
                  />
                </>
              )}
              {f.type === "select" && (
                <>
                  <Label>{f.label}{f.required && <span className="text-rose-500 ml-1">*</span>}</Label>
                  <Select value={values[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                    <SelectTrigger data-testid={`content-field-${f.key}`}>
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
              {f.type === "image" && (
                <ImageUploader
                  label={f.label}
                  value={values[f.key] || ""}
                  onChange={(url) => set(f.key, url)}
                />
              )}
              {f.type === "rich" && (
                <>
                  <Label>{f.label}</Label>
                  <div className="mt-1.5">
                    <RichTextEditor
                      value={values[f.key] || ""}
                      onChange={(html) => set(f.key, html)}
                      placeholder="Write your content here…"
                    />
                  </div>
                </>
              )}
            </div>
          ))}

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="content-cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#0B1B3D] hover:bg-[#1E3A8A] disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-md"
              data-testid="content-save-button"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContentManager;
